import { API_URL } from '../config';
import React, { useState, useEffect } from 'react';
import {
  BarChart3, RefreshCw, Settings, MessageSquare, TrendingUp,
  ChevronDown, ChevronUp, Users, CheckCircle, XCircle, Star,
  Clock, Save, Eye, Send, X
} from 'lucide-react';
import toast from 'react-hot-toast';
import axios from 'axios';
import StatCard from '../components/StatCard';
import DonutChart from '../components/DonutChart';


const AdminPesquisas = () => {
  const [activeTab, setActiveTab] = useState('csat');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [periodo, setPeriodo] = useState(30);

  // Config state
  const [config, setConfig] = useState({});

  // CSAT state
  const [csatStats, setCsatStats] = useState(null);
  const [csatRespostas, setCsatRespostas] = useState([]);

  // NPS state
  const [npsStats, setNpsStats] = useState(null);
  const [npsRespostas, setNpsRespostas] = useState([]);

  const [expandedCards, setExpandedCards] = useState({});
  const [previewHtml, setPreviewHtml] = useState('');
  const [previewTipo, setPreviewTipo] = useState('');
  const [showPreview, setShowPreview] = useState(false);
  const [enviandoTeste, setEnviandoTeste] = useState(false);
  const [emailTeste, setEmailTeste] = useState('');

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    carregarDados();
  }, [activeTab, periodo]);

  const carregarDados = async () => {
    setLoading(true);
    try {
      if (activeTab === 'config') {
        const res = await axios.get(`${API_URL}/pesquisas/config`, { headers });
        setConfig(res.data);
      } else if (activeTab === 'csat') {
        const [stats, respostas] = await Promise.all([
          axios.get(`${API_URL}/pesquisas/csat/estatisticas?dias=${periodo}`, { headers }),
          axios.get(`${API_URL}/pesquisas/csat/respostas?dias=${periodo}&limite=100`, { headers })
        ]);
        setCsatStats(stats.data);
        setCsatRespostas(respostas.data);
      } else if (activeTab === 'nps') {
        const [stats, respostas] = await Promise.all([
          axios.get(`${API_URL}/pesquisas/nps/estatisticas?dias=${periodo}`, { headers }),
          axios.get(`${API_URL}/pesquisas/nps/respostas?dias=${periodo}&limite=100`, { headers })
        ]);
        setNpsStats(stats.data);
        setNpsRespostas(respostas.data);
      }
    } catch (error) {
      toast.error('Erro ao carregar dados');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  // ========== CONFIG HELPERS ==========

  const getConfigVal = (grupo, chave) => {
    return config[grupo]?.[chave]?.valor || '';
  };

  const setConfigVal = (grupo, chave, valor) => {
    setConfig(prev => ({
      ...prev,
      [grupo]: {
        ...prev[grupo],
        [chave]: { ...prev[grupo]?.[chave], valor }
      }
    }));
  };

  const salvarConfig = async () => {
    setSaving(true);
    try {
      const payload = {};
      for (const [grupo, campos] of Object.entries(config)) {
        if (typeof campos === 'object') {
          payload[grupo] = {};
          for (const [chave, dados] of Object.entries(campos)) {
            payload[grupo][chave] = dados?.valor ?? dados;
          }
        }
      }
      await axios.put(`${API_URL}/pesquisas/config`, payload, { headers });
      toast.success('Configuracoes salvas!');
    } catch (error) {
      toast.error('Erro ao salvar');
    } finally {
      setSaving(false);
    }
  };

  // ========== FORMATTERS ==========

  const formatarData = (data) => {
    if (!data) return '-';
    return new Date(data).toLocaleDateString('pt-BR', {
      day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit'
    });
  };

  const getEmojiNota = (nota) => {
    const map = { 1: '\u{1F621}', 2: '\u{1F615}', 3: '\u{1F610}', 4: '\u{1F642}', 5: '\u{1F60D}' };
    return map[nota] || nota;
  };

  const getCorNPS = (score) => {
    if (score >= 75) return '#27ae60';
    if (score >= 50) return '#f39c12';
    if (score >= 0) return '#e67e22';
    return '#e74c3c';
  };

  const toggleCard = (id) => setExpandedCards(prev => ({ ...prev, [id]: !prev[id] }));

  const abrirPreview = async (tipo) => {
    try {
      const res = await axios.get(`${API_URL}/pesquisas/${tipo}/preview`, { headers, responseType: 'text' });
      setPreviewHtml(res.data);
      setPreviewTipo(tipo);
      setEmailTeste('');
      setShowPreview(true);
    } catch (error) {
      toast.error('Erro ao carregar preview');
    }
  };

  const enviarEmailTeste = async () => {
    if (!emailTeste) {
      toast.error('Informe um email');
      return;
    }
    setEnviandoTeste(true);
    try {
      await axios.post(`${API_URL}/pesquisas/${previewTipo}/enviar-teste`, { email: emailTeste }, { headers });
      toast.success(`Email de teste enviado para ${emailTeste}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao enviar teste');
    } finally {
      setEnviandoTeste(false);
    }
  };

  // ========== RENDER ==========

  if (loading && activeTab === 'config' && !Object.keys(config).length) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
        <RefreshCw size={32} style={{ animation: 'spin 1s linear infinite' }} />
        <p style={{ marginLeft: '10px' }}>Carregando...</p>
      </div>
    );
  }

  return (
    <div style={{ padding: '1.5rem', maxWidth: '1200px', margin: '0 auto' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem', flexWrap: 'wrap', gap: '1rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <BarChart3 size={28} color="var(--brand, #20a490)" />
          <div>
            <h1 style={{ fontSize: '1.5rem', fontWeight: '700', margin: 0 }}>Pesquisas</h1>
            <p style={{ fontSize: '0.85rem', color: '#888', margin: 0 }}>Configuracao e resultados de CSAT e NPS</p>
          </div>
        </div>

        {activeTab !== 'config' && (
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            {[7, 15, 30, 60, 90].map(d => (
              <button key={d} onClick={() => setPeriodo(d)} style={{
                padding: '6px 12px', borderRadius: '6px', border: 'none', cursor: 'pointer',
                backgroundColor: periodo === d ? 'var(--brand, #20a490)' : '#f0f0f0',
                color: periodo === d ? 'white' : '#666', fontSize: '13px', fontWeight: '500'
              }}>{d}d</button>
            ))}
            <button onClick={carregarDados} style={{ padding: '6px', borderRadius: '6px', border: 'none', cursor: 'pointer', backgroundColor: '#f0f0f0' }}>
              <RefreshCw size={16} />
            </button>
          </div>
        )}
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: '0', marginBottom: '1.5rem', borderBottom: '2px solid #e0e0e0' }}>
        {[
          { id: 'csat', label: 'Satisfacao (CSAT)', icon: <Star size={16} /> },
          { id: 'nps', label: 'NPS', icon: <TrendingUp size={16} /> },
          { id: 'config', label: 'Configuracoes', icon: <Settings size={16} /> },
        ].map(tab => (
          <button key={tab.id} onClick={() => setActiveTab(tab.id)} style={{
            display: 'flex', alignItems: 'center', gap: '6px', padding: '10px 20px',
            border: 'none', cursor: 'pointer', fontSize: '14px', fontWeight: '500',
            backgroundColor: 'transparent',
            color: activeTab === tab.id ? 'var(--brand, #20a490)' : '#888',
            borderBottom: activeTab === tab.id ? '2px solid var(--brand, #20a490)' : '2px solid transparent',
            marginBottom: '-2px'
          }}>
            {tab.icon} {tab.label}
          </button>
        ))}
      </div>

      {/* ========== TAB: CONFIG ========== */}
      {activeTab === 'config' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
          {/* CSAT Config Card */}
          <div style={styles.configCard}>
            <div style={styles.configHeader}>
              <div>
                <h3 style={styles.configTitle}><Star size={20} color="#f39c12" /> Pesquisa de Satisfacao (CSAT)</h3>
                <p style={styles.configDesc}>Enviada imediatamente ao finalizar cada atendimento. Pergunta se o problema foi resolvido e pede nota de 1 a 5 para o atendente.</p>
                <button onClick={() => abrirPreview('csat')} style={styles.previewBtn}>
                  <Eye size={14} /> Preview do email
                </button>
              </div>
              <label style={styles.toggleLabel}>
                <input
                  type="checkbox"
                  checked={getConfigVal('csat', 'ativo') === 'true'}
                  onChange={(e) => setConfigVal('csat', 'ativo', e.target.checked ? 'true' : 'false')}
                  style={styles.toggleInput}
                />
                <span style={{ ...styles.toggleSlider, backgroundColor: getConfigVal('csat', 'ativo') === 'true' ? 'var(--brand, #20a490)' : '#ccc' }}>
                  <span style={{ ...styles.toggleKnob, transform: getConfigVal('csat', 'ativo') === 'true' ? 'translateX(24px)' : 'translateX(2px)' }} />
                </span>
              </label>
            </div>
            {getConfigVal('csat', 'ativo') === 'true' && (
              <div style={styles.configBody}>
                <div style={styles.configRow}>
                  <label style={styles.fieldLabel}>Modo</label>
                  <select value={getConfigVal('csat', 'modo')} onChange={(e) => setConfigVal('csat', 'modo', e.target.value)} style={styles.select}>
                    <option value="teste">Teste</option>
                    <option value="producao">Producao</option>
                  </select>
                </div>
                {getConfigVal('csat', 'modo') === 'teste' && (
                  <div style={styles.configRow}>
                    <label style={styles.fieldLabel}>Email de teste</label>
                    <input type="email" value={getConfigVal('csat', 'email_teste')} onChange={(e) => setConfigVal('csat', 'email_teste', e.target.value)} style={styles.input} placeholder="email@teste.com" />
                  </div>
                )}
                <div style={styles.configRow}>
                  <div>
                    <label style={styles.fieldLabel}>Expiracao (dias)</label>
                    <p style={styles.fieldHint}>Dias ate o link da pesquisa expirar</p>
                  </div>
                  <input type="number" value={getConfigVal('csat', 'expiracao_dias')} onChange={(e) => setConfigVal('csat', 'expiracao_dias', e.target.value)} style={{ ...styles.input, width: '80px' }} min="1" max="30" />
                </div>
              </div>
            )}
          </div>

          {/* NPS Config Card */}
          <div style={styles.configCard}>
            <div style={styles.configHeader}>
              <div>
                <h3 style={styles.configTitle}><TrendingUp size={20} color="#27ae60" /> Net Promoter Score (NPS)</h3>
                <p style={styles.configDesc}>Pergunta "Recomendaria a operadora?", de 0 a 10. Enviada a cada X atendimentos finalizados: com X=3, o beneficiário recebe 1 NPS após 3 CSAT.</p>
                <button onClick={() => abrirPreview('nps')} style={styles.previewBtn}>
                  <Eye size={14} /> Preview do email
                </button>
              </div>
              <label style={styles.toggleLabel}>
                <input
                  type="checkbox"
                  checked={getConfigVal('nps', 'ativo') === 'true'}
                  onChange={(e) => setConfigVal('nps', 'ativo', e.target.checked ? 'true' : 'false')}
                  style={styles.toggleInput}
                />
                <span style={{ ...styles.toggleSlider, backgroundColor: getConfigVal('nps', 'ativo') === 'true' ? 'var(--brand, #20a490)' : '#ccc' }}>
                  <span style={{ ...styles.toggleKnob, transform: getConfigVal('nps', 'ativo') === 'true' ? 'translateX(24px)' : 'translateX(2px)' }} />
                </span>
              </label>
            </div>
            {getConfigVal('nps', 'ativo') === 'true' && (
              <div style={styles.configBody}>
                <div style={styles.configRow}>
                  <label style={styles.fieldLabel}>Modo</label>
                  <select value={getConfigVal('nps', 'modo')} onChange={(e) => setConfigVal('nps', 'modo', e.target.value)} style={styles.select}>
                    <option value="teste">Teste</option>
                    <option value="producao">Producao</option>
                  </select>
                </div>
                {getConfigVal('nps', 'modo') === 'teste' && (
                  <div style={styles.configRow}>
                    <label style={styles.fieldLabel}>Email de teste</label>
                    <input type="email" value={getConfigVal('nps', 'email_teste')} onChange={(e) => setConfigVal('nps', 'email_teste', e.target.value)} style={styles.input} placeholder="email@teste.com" />
                  </div>
                )}
                <div style={styles.configRow}>
                  <div>
                    <label style={styles.fieldLabel}>Frequencia (atendimentos finalizados)</label>
                    <p style={styles.fieldHint}>A cada {getConfigVal('nps', 'frequencia') || '3'} atendimentos finalizados do mesmo beneficiario, um NPS sera enviado</p>
                  </div>
                  <input type="number" value={getConfigVal('nps', 'frequencia')} onChange={(e) => setConfigVal('nps', 'frequencia', e.target.value)} style={{ ...styles.input, width: '80px' }} min="1" max="50" />
                </div>
                <div style={styles.configRow}>
                  <div>
                    <label style={styles.fieldLabel}>Delay apos gatilho (horas)</label>
                    <p style={styles.fieldHint}>Tempo de espera antes de enviar o email NPS apos atingir a frequencia</p>
                  </div>
                  <input type="number" value={getConfigVal('nps', 'delay_horas')} onChange={(e) => setConfigVal('nps', 'delay_horas', e.target.value)} style={{ ...styles.input, width: '80px' }} min="1" max="168" />
                </div>
                <div style={styles.configRow}>
                  <div>
                    <label style={styles.fieldLabel}>Expiracao (dias)</label>
                    <p style={styles.fieldHint}>Dias ate o link da pesquisa expirar</p>
                  </div>
                  <input type="number" value={getConfigVal('nps', 'expiracao_dias')} onChange={(e) => setConfigVal('nps', 'expiracao_dias', e.target.value)} style={{ ...styles.input, width: '80px' }} min="1" max="30" />
                </div>
              </div>
            )}
          </div>

          {/* Geral Config Card */}
          <div style={styles.configCard}>
            <div style={styles.configHeader}>
              <div>
                <h3 style={styles.configTitle}><Clock size={20} color="#888" /> Configuracoes Gerais</h3>
                <p style={styles.configDesc}>Evita excesso de pesquisas para o mesmo beneficiario.</p>
              </div>
            </div>
            <div style={styles.configBody}>
              <div style={styles.configRow}>
                <div>
                  <label style={styles.fieldLabel}>Cooldown entre pesquisas (dias)</label>
                  <p style={styles.fieldHint}>Intervalo minimo entre pesquisas para o mesmo beneficiario (evita excesso de emails)</p>
                </div>
                <input type="number" value={getConfigVal('geral', 'dias')} onChange={(e) => setConfigVal('geral', 'dias', e.target.value)} style={{ ...styles.input, width: '80px' }} min="1" max="365" />
              </div>
            </div>
          </div>

          {/* Botao Salvar */}
          <button onClick={salvarConfig} disabled={saving} style={{
            display: 'flex', alignItems: 'center', gap: '8px', justifyContent: 'center',
            padding: '12px 30px', backgroundColor: 'var(--brand, #20a490)', color: 'var(--brand-ink, #fff)', border: 'none',
            borderRadius: '8px', fontSize: '16px', fontWeight: '600', cursor: 'pointer',
            alignSelf: 'flex-start', opacity: saving ? 0.7 : 1
          }}>
            <Save size={18} />
            {saving ? 'Salvando...' : 'Salvar configuracoes'}
          </button>
        </div>
      )}

      {/* ========== TAB: CSAT ========== */}
      {activeTab === 'csat' && (
        <div>
          {loading ? (
            <div style={{ textAlign: 'center', padding: '40px' }}>
              <RefreshCw size={32} style={{ animation: 'spin 1s linear infinite' }} />
            </div>
          ) : csatStats ? (
            <>
              {/* Stats Cards */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem', marginBottom: '1.5rem' }}>
                <StatCard title="Enviadas" value={csatStats.total_enviados} icon={<MessageSquare size={20} />} color="#3498db" />
                <StatCard title="Respondidas" value={csatStats.total_respondidos} icon={<CheckCircle size={20} />} color="#27ae60" />
                <StatCard title="Taxa Resposta" value={`${csatStats.taxa_resposta}%`} icon={<Users size={20} />} color="#f39c12" />
                <StatCard title="Media Nota" value={csatStats.media_nota ? `${getEmojiNota(Math.round(csatStats.media_nota))} ${csatStats.media_nota}` : '-'} icon={<Star size={20} />} color="var(--brand, #20a490)" />
                <StatCard title="Problemas Resolvidos" value={`${csatStats.taxa_resolucao}%`} icon={<CheckCircle size={20} />} color="#27ae60" />
              </div>

              {/* Ranking por atendente */}
              {csatStats.por_atendente?.length > 0 && (
                <div style={styles.section}>
                  <h3 style={styles.sectionTitle}><Users size={18} /> Ranking por Atendente</h3>
                  <table style={styles.table}>
                    <thead>
                      <tr>
                        <th style={styles.th}>#</th>
                        <th style={styles.th}>Colaborador</th>
                        <th style={styles.th}>Media</th>
                        <th style={styles.th}>Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {csatStats.por_atendente.map((a, i) => (
                        <tr key={i} style={styles.tr}>
                          <td style={styles.td}>{i + 1}</td>
                          <td style={styles.td}>{a.nome}</td>
                          <td style={styles.td}>
                            <span style={{ fontSize: '18px' }}>{getEmojiNota(Math.round(a.media_nota))}</span>
                            {' '}{a.media_nota}
                          </td>
                          <td style={styles.td}>{a.total}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Respostas recentes */}
              <div style={styles.section}>
                <h3 style={styles.sectionTitle}><MessageSquare size={18} /> Respostas Recentes</h3>
                {csatRespostas.length === 0 ? (
                  <p style={{ color: '#888', textAlign: 'center', padding: '20px' }}>Nenhuma resposta no periodo</p>
                ) : (
                  csatRespostas.map((r, i) => (
                    <div key={i} style={styles.respostaCard} onClick={() => toggleCard(`csat-${i}`)}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                          <span style={{ fontSize: '28px' }}>{getEmojiNota(r.nota)}</span>
                          <div>
                            <span style={{ fontWeight: '600', fontSize: '14px' }}>{r.protocolo}</span>
                            <span style={{ color: '#888', fontSize: '13px', marginLeft: '10px' }}>{r.atendente_nome}</span>
                          </div>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          {r.problema_resolvido ? (
                            <span style={{ color: '#27ae60', fontSize: '12px', display: 'flex', alignItems: 'center', gap: '4px' }}><CheckCircle size={14} /> Resolvido</span>
                          ) : (
                            <span style={{ color: '#e74c3c', fontSize: '12px', display: 'flex', alignItems: 'center', gap: '4px' }}><XCircle size={14} /> Nao resolvido</span>
                          )}
                          <span style={{ fontSize: '12px', color: '#aaa' }}>{formatarData(r.respondido_em)}</span>
                          {expandedCards[`csat-${i}`] ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                        </div>
                      </div>
                      {expandedCards[`csat-${i}`] && r.motivo && (
                        <div style={{ marginTop: '10px', padding: '10px', backgroundColor: '#f9f9f9', borderRadius: '6px', fontSize: '14px', color: '#555' }}>
                          {r.motivo}
                        </div>
                      )}
                    </div>
                  ))
                )}
              </div>
            </>
          ) : null}
        </div>
      )}

      {/* ========== TAB: NPS ========== */}
      {activeTab === 'nps' && (
        <div>
          {loading ? (
            <div style={{ textAlign: 'center', padding: '40px' }}>
              <RefreshCw size={32} style={{ animation: 'spin 1s linear infinite' }} />
            </div>
          ) : npsStats ? (
            <>
              {/* NPS Score */}
              <div style={{ display: 'flex', gap: '1.5rem', marginBottom: '1.5rem', flexWrap: 'wrap' }}>
                <div style={{ flex: '1', minWidth: '250px', backgroundColor: 'white', borderRadius: '12px', padding: '30px', textAlign: 'center', boxShadow: '0 1px 4px rgba(0,0,0,0.08)' }}>
                  <p style={{ fontSize: '14px', color: '#888', marginBottom: '10px' }}>NPS Score</p>
                  <div style={{
                    width: '120px', height: '120px', borderRadius: '50%', margin: '0 auto 15px',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    border: `6px solid ${getCorNPS(npsStats.nps_score)}`, fontSize: '36px', fontWeight: '700',
                    color: getCorNPS(npsStats.nps_score)
                  }}>
                    {npsStats.nps_score}
                  </div>
                  <p style={{ fontSize: '16px', fontWeight: '600', color: getCorNPS(npsStats.nps_score) }}>
                    {npsStats.nps_score >= 75 ? 'Excelente' : npsStats.nps_score >= 50 ? 'Muito Bom' : npsStats.nps_score >= 0 ? 'Razoavel' : 'Precisa Melhorar'}
                  </p>
                </div>
                <div style={{ flex: '1', minWidth: '250px' }}>
                  <DonutChart
                    data={[
                      { label: `Promotores (${npsStats.promotores})`, value: npsStats.total_respondidos > 0 ? Math.round(npsStats.promotores / npsStats.total_respondidos * 100) : 0, color: '#27ae60' },
                      { label: `Passivos (${npsStats.passivos})`, value: npsStats.total_respondidos > 0 ? Math.round(npsStats.passivos / npsStats.total_respondidos * 100) : 0, color: '#f39c12' },
                      { label: `Detratores (${npsStats.detratores})`, value: npsStats.total_respondidos > 0 ? Math.round(npsStats.detratores / npsStats.total_respondidos * 100) : 0, color: '#e74c3c' },
                    ]}
                    title="Distribuicao"
                  />
                </div>
              </div>

              {/* Stats */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '1rem', marginBottom: '1.5rem' }}>
                <StatCard title="Enviadas" value={npsStats.total_enviados} icon={<MessageSquare size={20} />} color="#3498db" />
                <StatCard title="Respondidas" value={npsStats.total_respondidos} icon={<CheckCircle size={20} />} color="#27ae60" />
                <StatCard title="Taxa Resposta" value={`${npsStats.taxa_resposta}%`} icon={<Users size={20} />} color="#f39c12" />
                <StatCard title="Media" value={npsStats.media_nota || '-'} icon={<Star size={20} />} color="var(--brand, #20a490)" />
              </div>

              {/* Respostas */}
              <div style={styles.section}>
                <h3 style={styles.sectionTitle}><MessageSquare size={18} /> Respostas Recentes</h3>
                {npsRespostas.length === 0 ? (
                  <p style={{ color: '#888', textAlign: 'center', padding: '20px' }}>Nenhuma resposta no periodo</p>
                ) : (
                  npsRespostas.map((r, i) => (
                    <div key={i} style={styles.respostaCard} onClick={() => toggleCard(`nps-${i}`)}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                          <div style={{
                            width: '36px', height: '36px', borderRadius: '8px', display: 'flex',
                            alignItems: 'center', justifyContent: 'center', color: 'white', fontWeight: '700',
                            backgroundColor: r.nota >= 9 ? '#27ae60' : r.nota >= 7 ? '#f39c12' : '#e74c3c'
                          }}>{r.nota}</div>
                          <span style={{ fontWeight: '500' }}>{r.beneficiario_nome || 'Anonimo'}</span>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <span style={{ fontSize: '12px', color: '#aaa' }}>{formatarData(r.respondido_em)}</span>
                          {r.feedback && (expandedCards[`nps-${i}`] ? <ChevronUp size={16} /> : <ChevronDown size={16} />)}
                        </div>
                      </div>
                      {expandedCards[`nps-${i}`] && r.feedback && (
                        <div style={{ marginTop: '10px', padding: '10px', backgroundColor: '#f9f9f9', borderRadius: '6px', fontSize: '14px', color: '#555' }}>
                          {r.feedback}
                        </div>
                      )}
                    </div>
                  ))
                )}
              </div>
            </>
          ) : null}
        </div>
      )}
      {/* ========== MODAL PREVIEW ========== */}
      {showPreview && (
        <div style={styles.modalOverlay} onClick={() => setShowPreview(false)}>
          <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <h3 style={{ margin: 0, fontSize: '16px' }}>
                <Eye size={18} style={{ verticalAlign: 'middle', marginRight: '8px' }} />
                Preview - Email {previewTipo.toUpperCase()}
              </h3>
              <button onClick={() => setShowPreview(false)} style={styles.modalClose}>
                <X size={20} />
              </button>
            </div>
            <div style={styles.modalBody}>
              <iframe
                srcDoc={previewHtml}
                style={{ width: '100%', height: '500px', border: 'none', borderRadius: '8px' }}
                title="Preview email"
              />
            </div>
            <div style={styles.modalFooter}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flex: 1 }}>
                <input
                  type="email"
                  value={emailTeste}
                  onChange={(e) => setEmailTeste(e.target.value)}
                  placeholder="Email para teste..."
                  style={{ ...styles.input, flex: 1, margin: 0 }}
                />
                <button onClick={enviarEmailTeste} disabled={enviandoTeste} style={{
                  display: 'flex', alignItems: 'center', gap: '6px', padding: '8px 20px',
                  backgroundColor: 'var(--brand, #20a490)', color: 'var(--brand-ink, #fff)', border: 'none', borderRadius: '6px',
                  fontSize: '14px', cursor: 'pointer', whiteSpace: 'nowrap',
                  opacity: enviandoTeste ? 0.7 : 1
                }}>
                  <Send size={14} />
                  {enviandoTeste ? 'Enviando...' : 'Enviar teste'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const styles = {
  configCard: {
    backgroundColor: 'white',
    borderRadius: '12px',
    boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
    overflow: 'hidden',
  },
  configHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '20px 24px',
    borderBottom: '1px solid #f0f0f0',
  },
  configTitle: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontSize: '16px',
    fontWeight: '600',
    margin: '0 0 4px 0',
  },
  configDesc: {
    fontSize: '13px',
    color: '#888',
    margin: 0,
  },
  fieldHint: {
    fontSize: '12px',
    color: '#aaa',
    margin: '2px 0 0 0',
  },
  configBody: {
    padding: '20px 24px',
    display: 'flex',
    flexDirection: 'column',
    gap: '14px',
  },
  configRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  fieldLabel: {
    fontSize: '14px',
    color: '#555',
    minWidth: '200px',
  },
  input: {
    padding: '8px 12px',
    borderRadius: '6px',
    border: '1px solid #ddd',
    fontSize: '14px',
    flex: 1,
  },
  select: {
    padding: '8px 12px',
    borderRadius: '6px',
    border: '1px solid #ddd',
    fontSize: '14px',
    backgroundColor: 'white',
  },
  toggleLabel: {
    position: 'relative',
    display: 'inline-block',
    width: '50px',
    height: '26px',
    cursor: 'pointer',
    flexShrink: 0,
  },
  toggleInput: {
    opacity: 0,
    width: 0,
    height: 0,
    position: 'absolute',
  },
  toggleSlider: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    borderRadius: '26px',
    transition: '0.3s',
    display: 'block',
  },
  toggleKnob: {
    position: 'absolute',
    top: '2px',
    width: '22px',
    height: '22px',
    backgroundColor: 'white',
    borderRadius: '50%',
    transition: '0.3s',
    boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
  },
  section: {
    backgroundColor: 'white',
    borderRadius: '12px',
    padding: '20px',
    marginBottom: '1.5rem',
    boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
  },
  sectionTitle: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontSize: '16px',
    fontWeight: '600',
    marginBottom: '15px',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
  },
  th: {
    textAlign: 'left',
    padding: '10px 12px',
    borderBottom: '2px solid #e0e0e0',
    fontSize: '13px',
    color: '#888',
    fontWeight: '600',
  },
  tr: {
    borderBottom: '1px solid #f0f0f0',
  },
  td: {
    padding: '10px 12px',
    fontSize: '14px',
  },
  respostaCard: {
    padding: '12px 16px',
    borderBottom: '1px solid #f0f0f0',
    cursor: 'pointer',
  },
  previewBtn: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    padding: '6px 14px',
    backgroundColor: '#f0f0f0',
    border: '1px solid #ddd',
    borderRadius: '6px',
    fontSize: '13px',
    color: '#555',
    cursor: 'pointer',
    marginTop: '8px',
  },
  modalOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0,0,0,0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 9999,
  },
  modalContent: {
    backgroundColor: 'white',
    borderRadius: '12px',
    width: '95%',
    maxWidth: '700px',
    maxHeight: '90vh',
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
  },
  modalHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '16px 20px',
    borderBottom: '1px solid #eee',
  },
  modalClose: {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    color: '#888',
    padding: '4px',
  },
  modalBody: {
    padding: '20px',
    overflow: 'auto',
    flex: 1,
    backgroundColor: '#f5f5f5',
  },
  modalFooter: {
    padding: '16px 20px',
    borderTop: '1px solid #eee',
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
  },
};

export default AdminPesquisas;
